/*
 * ------------------------------------------------------------------------------------------
 * - External Imports -
 * ------------------------------------------------------------------------------------------
 */
import * as xLicense from '@mui/x-license';
import { createRoot } from 'react-dom/client';

/*
 * ------------------------------------------------------------------------------------------
 * - Internal Imports -
 * ------------------------------------------------------------------------------------------
 */

import { FormDefinitionCollectionResponse, FormDefinitionGetCollection, FormsFeatureContentProvider, FormsFeatureProvider, GetFormMetadata, GetFormMetadataResponse, IReturn, IUserResponse } from '@ngt/forms';
import * as Sentry from "@sentry/react";
import { JsonServiceClient } from '@servicestack/client';
import { StrictMode, useEffect } from 'react';
import { BrowserRouter, createRoutesFromChildren, matchRoutes, Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import App from './App';
import { AppSettings } from './AppSettings';
import { UserGetSingle } from './api/dtos';
import ErrorBoundary from './components/error/ErrorBoundary';
import createFormsFeature from './forms';
import StagePlanViewPublic from './pages/stageplan/StagePlanViewPublic';
import { createSentryAnonymousUser } from './utility/errors';

/*
 * ------------------------------------------------------------------------------------------
 * - Render -
 * ------------------------------------------------------------------------------------------
 */

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}

xLicense.LicenseInfo.setLicenseKey('19496e787983cdcfb6235fa20af1899bTz0xMTYyOTksRT0xNzg0NDE5MTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y');
// xLicense.LicenseInfo.setLicenseKey(process.env.MUI_X_LICENSE_KEY!);

if (AppSettings.sentry.dsn) {
    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the 
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes
        }),
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];

    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }
    
    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
            'localhost', 
            /^\//,
            new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });
    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());
}

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const startApp = (user?: IUserResponse, formMetadata?: GetFormMetadataResponse, formDefinitions?: FormDefinitionCollectionResponse) => {

    if (/stageplan.aspx/.test(global.location.href)) {
        return (
            <StrictMode>
                <BrowserRouter>
                    <SentryRoutes>
                        <Route
                            path="/stageplan.aspx"
                            element={
                                <StagePlanViewPublic />
                            }
                        />
                    </SentryRoutes>
                </BrowserRouter>
            </StrictMode>
        )
    }

    const formsFeature = createFormsFeature(user, formMetadata, formDefinitions);
    
    return(
        <StrictMode>
            <FormsFeatureProvider 
                value={formsFeature}
                configuration={{
                    authenticatedUser: {
                        revalidateOnMount: false
                    },
                    formDefinitions: {
                        revalidateOnMount: false
                    },
                    formMetadata: {
                        revalidateOnMount: false
                    }
                }}
            >
                <FormsFeatureContentProvider>
                    <App />
                </FormsFeatureContentProvider>
            </FormsFeatureProvider>   
        </StrictMode>
    )
}

const loadData = async <T,>(client: JsonServiceClient, request: IReturn<T>) => {
    try {
        return await client.get(request);
    }
    catch (error) {
        console.error(error);
    }

    return undefined;
}

const bootstrap = async () => {
    if (AppSettings.debug) {
        const client = new JsonServiceClient();

        const [
            user, 
            formMetadata,
            formDefinitions
        ] = await Promise.all([
            loadData(client, new UserGetSingle()),
            loadData(client, new GetFormMetadata()),
            loadData(client, new FormDefinitionGetCollection())
        ]);
        
        return startApp(user, formMetadata, formDefinitions);
    } else {
        const user = global.AUTH as IUserResponse | undefined;
        const formMetadata = global.FORM_METADATA as GetFormMetadataResponse | undefined;
        const formDefinitions = (global as any).FORM_DEFINITIONS as FormDefinitionCollectionResponse | undefined;

        return startApp(user, formMetadata, formDefinitions);
    }
}


document.addEventListener('DOMContentLoaded', async (_event) => {  
    const root = AppSettings.sentry.dsn ? 
        createRoot(
            document.getElementById('root')!,
            {
                // Callback called when React automatically recovers from errors.
                onRecoverableError: Sentry.reactErrorHandler(),
            }
        ) :
        createRoot(document.getElementById('root')!);

    const content = await bootstrap();
    
    root.render(
        <StrictMode>
            <Sentry.ErrorBoundary fallback={ErrorBoundary}>
                {content}
            </Sentry.ErrorBoundary>
        </StrictMode>
    )
});

bootstrap();